import { z } from "zod";

import { INCOME_CATEGORY_VALUES } from "../catalog/income-categories.js";
import { RECURRENCE_INTERVALS } from "../value-objects/recurrence.js";

/**
 * The wire contract for the income routes.
 *
 * The category and interval enums are DERIVED from the catalogue and the kernel's interval table, not
 * restated. A hand-written union here is the obvious thing to write when the compiler asks for one,
 * and it is exactly the second copy the catalogue's git-grep guard exists to prevent — a form
 * offering eleven options while the chart draws twelve surfaces as a category that silently cannot be
 * chosen.
 */

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Enter a date as YYYY-MM-DD.");

/**
 * Money crosses the wire as a DECIMAL STRING, not a number.
 *
 * `numeric(15,2)` outruns the range where a double represents every cent exactly, and JSON has only
 * doubles. A string keeps the value intact from the form to the column, and the one place it becomes
 * a float is `Valuation.toEuros()` on the way out, for display.
 *
 * NOTHING HERE REJECTS A PAST DATE. Backdating is a requirement (FR-013) — income's chart reaches
 * back before the household used GLADIS — and a "sanity check" added here would break the module's
 * defining capability while looking like hygiene.
 */
const amount = z
  .string()
  .regex(/^\d{1,15}(\.\d{1,2})?$/, "Enter an amount like 1234.56.")
  .refine((value) => Number(value) > 0, "Enter an amount greater than zero.");

const ownership = z.enum(["joint", "individual"]);
const category = z.enum(INCOME_CATEGORY_VALUES as [string, ...string[]]);
const interval = z.enum(RECURRENCE_INTERVALS as unknown as [string, ...string[]]);

export const IncomePageQuerySchema = z.object({
  granularity: z.enum(["year", "month"]).default("year"),
  months: z.coerce
    .number()
    .int()
    .refine((n) => [12, 24, 36].includes(n))
    .default(12),
});

export const CreateIncomeSourceRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  category,
  ownership,
  ownerId: z.string().uuid().nullable().default(null),
  amount,
  interval,
  effectiveFrom: isoDay,
});

export const UpdateIncomeSourceRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  category,
  ownership,
  ownerId: z.string().uuid().nullable().default(null),
});

export const RecordIncomeAmountRequestSchema = z.object({
  amount,
  interval,
  effectiveFrom: isoDay,
});

/** `null` un-ends the source. Ending and archiving are separate endpoints, deliberately. */
export const EndIncomeSourceRequestSchema = z.object({
  endedOn: isoDay.nullable(),
});

export const ArchiveIncomeSourceRequestSchema = z.object({
  archived: z.boolean(),
});

/**
 * `category`, `ownership` and `ownerId` are accepted but IGNORED when `incomeSourceId` is present.
 *
 * The server copies them from the source instead. That is the model rather than defensive coding: an
 * entry records a historical event, so its attribution is the source's at the moment it arrived, and
 * a client-supplied value would be a third answer to a question that already has one.
 */
export const RecordIncomeEntryRequestSchema = z.object({
  incomeSourceId: z.string().uuid().nullable().default(null),
  description: z.string().trim().min(1).max(160),
  amount,
  receivedOn: isoDay,
  category: category.default("other"),
  ownership: ownership.default("joint"),
  ownerId: z.string().uuid().nullable().default(null),
});

export const UpdateIncomeEntryRequestSchema = z.object({
  description: z.string().trim().min(1).max(160),
  amount,
  receivedOn: isoDay,
  category: category.optional(),
  ownership: ownership.optional(),
  ownerId: z.string().uuid().nullable().optional(),
});

export type IncomePageQuery = z.infer<typeof IncomePageQuerySchema>;
export type CreateIncomeSourceRequest = z.infer<typeof CreateIncomeSourceRequestSchema>;
export type UpdateIncomeSourceRequest = z.infer<typeof UpdateIncomeSourceRequestSchema>;
export type RecordIncomeAmountRequest = z.infer<typeof RecordIncomeAmountRequestSchema>;
export type EndIncomeSourceRequest = z.infer<typeof EndIncomeSourceRequestSchema>;
export type ArchiveIncomeSourceRequest = z.infer<typeof ArchiveIncomeSourceRequestSchema>;
export type RecordIncomeEntryRequest = z.infer<typeof RecordIncomeEntryRequestSchema>;
export type UpdateIncomeEntryRequest = z.infer<typeof UpdateIncomeEntryRequestSchema>;
